import React, { useState } from "react"
import { SchemaResponseResource } from "~/api2/dynamic_forms"
import { useDynamicFormsResource } from "~/hooks/useDynamicFormsResource"
import Button from "../../Button"
import { useMe } from "~/api2/hooks/useMe"
import titleCase from "~/utils/titleCase"
import { useConfirm } from "~/hooks/useConfirm"
import DropdownSelector from "~/components/ui/DropdownSelector/DropdownSelector"
import { useEffect } from "react"
import { debounce } from 'lodash'
import { useNavigate } from "react-router-dom"
import slugify from "~/utils/admin/slugify"

type FieldMeta = { name: string; type: string }

interface RouteProps {
  schema: SchemaResponseResource
}

const DynamicFormRoute = ({ schema }: RouteProps) => {
  return (
    <div className="">
      <DynamicFormIndex model={schema.model} fields={schema.fields} resource={schema.resource} />
    </div>
  )
}

interface IndexProps<T extends Record<string, unknown>> {
  model: string
  fields: FieldMeta[]
  resource: string
}

function DynamicFormIndex<T extends Record<string, unknown>>({ model, resource }: IndexProps<T>) {
  const { data: { camp_id: campId } } = useMe()
  const { showConfirm, ConfirmModal } = useConfirm()
  const navigate = useNavigate()
  const [perPage, setPerPage] = useState<25 | 50 | 100 | 200>(25)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    const pp = parseInt(sp.get("perPage")) || 25
    const allowed = [25, 50, 100, 200]
    setPerPage(allowed.includes(pp) ? pp as 25 | 50 | 100 | 200 : 25)
    const q = sp.get('q') || ''
    setSearch(q)
    setSearchInput(q)
    const p = parseInt(sp.get('page')) || 1
    setPage(p > 0 ? p : 1)
  }, [])
  
  const handlePerPageChange = (newVal: 25 | 50 | 100 | 200) => {
    setPerPage(newVal)
    const sp = new URLSearchParams(window.location.search)
    sp.set("perPage", newVal.toString())
    window.history.pushState({}, "", `${window.location.pathname}?${sp.toString()}`)
    setPage(1)
  }
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchInput(val)
    debouncedSetSearch(val)
    setPage(1)
  }
  
  const debouncedSetSearch = React.useCallback(debounce((val: string) => {
    setSearch(val)
    const sp = new URLSearchParams(window.location.search)
    if (val) sp.set('q', val); else sp.delete('q')
    window.history.pushState({}, '', `${window.location.pathname}?${sp.toString()}`)
  }, 400), [])
  
  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return
    setPage(newPage)
    const sp = new URLSearchParams(window.location.search)
    sp.set('page', newPage.toString())
    window.history.pushState({}, '', `${window.location.pathname}?${sp.toString()}`)
  }
  
  const {
    index,
    destroy: { mutate: deleteResource },
  } = useDynamicFormsResource(resource, campId, search || undefined, undefined, page, perPage)

  const handleSelectResource = (resource: T) => {
    const modelSlug = slugify(model)
    const url = `/admin/settings/${modelSlug}/${resource.id}`
    navigate(url)
  }

  const handleDeleteResource = async (resourceId: string) => {
    const isConfirmed = await showConfirm({
      title: "Are you sure you want to delete this resource?",
      description: "This action cannot be undone.",
      ctaLabel: "Remove",
      colorName: "red",
      ctaColorName: "red",
    })

    if (isConfirmed) {
      deleteResource(resourceId, {
        onError: (error) => {
          console.error("Error deleting resource:", error)
        },
      })
    }
  }

  // Calculate if pagination is needed
  const totalPages = Math.ceil((index.data?.total || 0) / perPage)
  const showPagination = totalPages > 1

  // TODO: handle errors and loading

  return (
    <>
      <div className="sticky top-[69px] bg-white z-10 p-4 border border-gray-200 shadow-sm rounded-lg">
        <div className="flex justify-between items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search by ID, name, label, email, etc."
              className="block w-80 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-600 focus:border-green-600 sm:text-sm"
            />
          </div>
          <div className="flex items-center gap-4">
            {showPagination && (
              <div className="flex items-center gap-2">
                <Button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className="bg-blue-500 text-white px-3 py-1 rounded">
                  Previous
                </Button>
                <span className="self-center">Page {page} of {totalPages}</span>
                <Button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} className="bg-blue-500 text-white px-3 py-1 rounded">
                  Next
                </Button>
              </div>
            )}
            {index.data?.total > 25 && (
              <div className="w-48">
                <DropdownSelector
                  value={perPage}
                  onChange={handlePerPageChange}
                  options={[
                    { value: 25, label: "25 per page" },
                    { value: 50, label: "50 per page" },
                    { value: 100, label: "100 per page" },
                    { value: 200, label: "200 per page" },
                  ]}
                  label="Items per page"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="pt-3">
        {index.isLoading ? (
          <>
            {Array.from({ length: perPage }).map((_, i) => (
              <div key={i} className="flex justify-between mb-4 animate-pulse py-1">
                <div className="h-10 bg-gray-200 rounded w-1/2"></div>
                <div className="flex gap-2">
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            ))}
          </>
        ) : index.data?.records?.length ? (
          index.data.records.map((resource) => (
            <div className="flex justify-between mb-4" key={resource.id}>
              <span>{`${(resource.id as string) || ''} - ${titleCase(((resource as any).name || (resource as any).label || (resource as any).kind) || "")}`}</span>
              <div className="">
                <Button onClick={() => handleSelectResource(resource)} className="bg-blue-500 text-white px-3 py-1 rounded">
                  Edit
                </Button>
                <Button onClick={async () => handleDeleteResource(resource.id)} className="bg-red-500 text-white px-3 py-1 rounded ml-2">
                  Delete
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-gray-500">No resources found for {model}</div>
        )}
      </div>
      <ConfirmModal />
    </>
  )
}

export default DynamicFormRoute
